import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useLanguageContext } from '@/i18n/language-context';
import { getLanguageDisplayName } from './utils/language-display-name';

/**
 * LanguageSelector Component
 *
 * A dropdown menu component that allows users to select their preferred language
 * from a list of available languages fetched from the API.
 *
 * @example
 * // Basic usage in a navigation bar
 * <nav className="flex items-center justify-between">
 *   <Logo />
 *   <div className="flex items-center gap-4">
 *     <LanguageSelector />
 *     <UserMenu />
 *   </div>
 * </nav>
 */

type LanguageSelectorProps = {
  /**
   * DOM node the dropdown content portals into. Pass a node that lives inside a
   * subtree with its own forced-light CSS variable overrides (e.g. the auth hero
   * page) so the popover isn't rendered with the app's dark-theme tokens.
   */
  portalContainer?: HTMLElement | null;
};

/** Switches to the default language when the saved one is not offered (any more). */
export const useEnsureSupportedLanguage = () => {
  const { currentLanguage, setLanguage, availableLanguages, isLoading } = useLanguageContext();
  useEffect(() => {
    if (!isLoading && availableLanguages?.length > 0) {
      const currentLanguageExists = availableLanguages.some(
        (lang) => lang.languageCode === currentLanguage
      );
      if (!currentLanguageExists) {
        const defaultLanguage = availableLanguages.find((lang) => lang.isDefault);
        if (defaultLanguage) setLanguage(defaultLanguage.languageCode);
      }
    }
  }, [availableLanguages, currentLanguage, isLoading, setLanguage]);
};

export const LanguageSelector = ({ portalContainer }: LanguageSelectorProps = {}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { currentLanguage, setLanguage, availableLanguages, isLoading } = useLanguageContext();

  useEffect(() => {
    if (!isLoading && availableLanguages?.length > 0) {
      const currentLanguageExists = availableLanguages.some(
        (lang) => lang.languageCode === currentLanguage
      );

      if (!currentLanguageExists) {
        const defaultLanguage = availableLanguages.find((lang) => lang.isDefault);
        if (defaultLanguage) {
          setLanguage(defaultLanguage.languageCode);
        }
      }
    }
  }, [availableLanguages, currentLanguage, isLoading, setLanguage]);

  const changeLanguage = async (newLanguageCode: string) => {
    await setLanguage(newLanguageCode);
  };

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild className="cursor-pointer">
        <div className="flex items-center gap-1 h-9 px-2 rounded-md hover:bg-card">
          {/* Until the list loads only the raw code is known ("en"): a placeholder instead. */}
          {isLoading ? (
            <Skeleton className="h-4 w-14" />
          ) : (
            <span className="text-sm font-semibold text-muted-foreground">
              {getLanguageDisplayName(
                availableLanguages?.find((lang) => lang.languageCode === currentLanguage)
                  ?.languageName ?? currentLanguage,
                currentLanguage
              )}
            </span>
          )}
          {isDropdownOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" container={portalContainer ?? undefined}>
        {isLoading && (
          <DropdownMenuItem disabled>
            <Skeleton className="h-4 w-24" />
          </DropdownMenuItem>
        )}
        {!isLoading &&
          (availableLanguages && availableLanguages.length > 0 ? (
            availableLanguages.map((lang, i) => (
              <div key={lang.itemId}>
                <DropdownMenuItem
                  className={cn({
                    'font-bold cursor-pointer': lang.languageCode === currentLanguage,
                  })}
                  onClick={() => changeLanguage(lang.languageCode)}
                >
                  {getLanguageDisplayName(lang.languageName, lang.languageCode)}{' '}
                  {lang.isDefault && '(Default)'}
                </DropdownMenuItem>
                {i !== availableLanguages.length - 1 && <DropdownMenuSeparator />}
              </div>
            ))
          ) : (
            <DropdownMenuItem disabled>{t('NO_LANGUAGES_AVAILABLE')}</DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
